import React, { useState, useEffect } from "react";
import { Spin } from "antd";
import { Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import CustomInput from "../../../CustomControls/CustomInput";
import swal from "sweetalert";
import { post, postFormData } from "../../../helpers/api_helper";
import CustomTextArea from "../../../CustomControls/CustomTextArea";

import { SVGICON } from "../../../constant/theme";
import {
  capitalizeString,
  errorMessage,
  successMessage,
  warningMessage,
} from "../CommonFunctions";
import {
  POST_CompanyLUTDocumentInsert,
  POST_CompanyLUTDocumentUpdate,
  POST_GetCompanyLUTDocumentDetailByID,
} from "../../../helpers/url_helper";
import moment from "moment";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";

const AddCompanyLUTDocument = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { PrimaryIdParam } = useParams();

  const [lUTCertificateAttachmentFile, setLUTCertificateAttachmentFile] =
    useState(null);

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const validationSchema = Yup.object().shape({
    LUTCertificate: Yup.string()
      .trim()
      .required("LUT Certificate is required field"),
    ValidityFromDate: Yup.string()
      .trim()
      .required("Validity From Date is required field"),
    ValidityToDate: Yup.string()
      .trim()
      .required("Validity To Date is required field"),
  });

  const formik = useFormik({
    validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      CompanyLUTDocumentIDEncrypt: PrimaryIdParam
        ? window.atob(PrimaryIdParam)
        : "",
      LUTCertificate: "",
      ValidityFromDate: new Date(),
      ValidityToDate: new Date(),
      LUTCertificateAttachmentFilePath: "",
      LUTCertificateAttachmentFilePathURL: "",
      LUTCertificateFileKey: "",
    },
    validateOnBlur: false,
  });

  const {
    values: {
      LUTCertificate,
      ValidityFromDate,
      ValidityToDate,
      LUTCertificateAttachmentFilePath,
      LUTCertificateAttachmentFilePathURL,
      LUTCertificateFileKey,
    },
  } = formik;

  const handleValidSubmit = (values) => {
    if (new Date(ValidityToDate) < new Date(ValidityFromDate)) {
      errorMessage("To Date cannot be earlier than From Date.");
      return;
    }
    if (
      !lUTCertificateAttachmentFile &&
      !values?.LUTCertificateAttachmentFilePathURL
    ) {
      errorMessage("Select Doc/PDF file!");
      return;
    }

    setIsLoading(true);
    let formData = new FormData();

    formData.append(
      "CompanyLUTDocumentIDEncrypt",
      values?.CompanyLUTDocumentIDEncrypt
    );
    formData.append("CompanyIDEncrypted", AuthUser?.CompanyIDEncrypt);
    formData.append("LUTCertificate", values?.LUTCertificate);

    formData.append(
      "ValidityFromDate",
      values?.ValidityFromDate
        ? moment(values?.ValidityFromDate)
            .format("YYYY-MM-DD")
            .replace(/^\-/, "")
        : ""
    );
    formData.append(
      "ValidityToDate",
      values?.ValidityToDate
        ? moment(values?.ValidityToDate).format("YYYY-MM-DD").replace(/^\-/, "")
        : ""
    );

    formData.append(
      "LUTCertificateAttachmentFilePathURL",
      values?.LUTCertificateAttachmentFilePathURL
    );
    formData.append(
      "LUTCertificateAttachmentFilePath",
      lUTCertificateAttachmentFile
    );
    formData.append(
      "CreatedByEncrypted",
      AuthUser?.ReferenceIDEncrypt ? AuthUser?.ReferenceIDEncrypt : "0"
    );
    formData.append(
      "ModifiedByEncrypt",
      AuthUser?.ReferenceIDEncrypt ? AuthUser?.ReferenceIDEncrypt : "0"
    );

    //console.log("Add", formData);

    postFormData(
      PrimaryIdParam
        ? POST_CompanyLUTDocumentUpdate
        : POST_CompanyLUTDocumentInsert,
      formData
    )
      .then((response) => {
        setIsLoading(true);
        if (response?.IsSuccess) {
          successMessage(response?.Message);
          setTimeout(() => {
            navigate("/company-lut-document");
          }, 1000);
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (ValidityToDate && ValidityToDate < ValidityFromDate) {
      formik.setFieldValue("ValidityToDate", ValidityFromDate);
    }
  }, [ValidityFromDate]);

  useEffect(() => {
    if (PrimaryIdParam) {
      post(POST_GetCompanyLUTDocumentDetailByID, {
        CompanyLUTDocumentIDEncrypt: window.atob(PrimaryIdParam),
      })
        .then((response) => {
          if (response?.IsSuccess) {
            formik.setValues({
              ...response,
              LUTCertificate: response?.LUTCertificate,
              ValidityFromDate: response?.ValidityFromDate
                ? new Date(response?.ValidityFromDate)
                : new Date(),
              ValidityToDate: response?.ValidityToDate
                ? new Date(response?.ValidityToDate)
                : new Date(),
            });
          }

          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const uploadLUTCertificateAttachmentFile = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      var pattern =
        /^(application\/(msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)|application\/pdf|image\/(jpeg|png|gif))$/i;

      if (!file.type.match(pattern)) {
        setLUTCertificateAttachmentFile(null);
        formik.setFieldValue(
          "LUTCertificateFileKey",
          Math.random().toString(9).substring(2, 9)
        );
        warningMessage("Select valid Doc/PDF/Image file!");
      } else {
        setLUTCertificateAttachmentFile(file);
      }
    } else {
      setLUTCertificateAttachmentFile(null);
      formik.setFieldValue(
        "LUTCertificateFileKey",
        Math.random().toString(9).substring(2, 9)
      );
    }
  };

  useEffect(() => {
    if (!ValidityFromDate) {
      formik.setFieldValue("ValidityFromDate", "");
    }
    if (!ValidityToDate) {
      formik.setFieldValue("ValidityToDate", "");
    }
  }, [ValidityFromDate, ValidityToDate]);

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin
          size="large"
          spinning={isLoading}
          tip={"Loading..."}
          style={{ position: "fixed" }}
        >
          <div className="table-responsive active-projects style-1 shorting">
            <div className="tbl-caption p-2">
              <h4 className="heading mb-0">
                {PrimaryIdParam ? "Edit" : "Add"} Company LUT Document
              </h4>
            </div>
          </div>
          <hr className="mt-0 mb-1"></hr>
          <div className="p-2 pt-0">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={3}>
                    <Field
                      label="LUT Certificate"
                      name="LUTCertificate"
                      placeholder={"Enter LUT Certificate"}
                      component={CustomInput}
                      required
                      maxLength={500}
                      autoFocus
                    />
                  </Col>

                  <Col lg={3}>
                    <div className="d-flex justify-content-between">
                      <label className="mb-1">
                        LUT Certificate Attachment File{" "}
                        <span className="text-danger">*</span>
                      </label>
                      {LUTCertificateAttachmentFilePath ? (
                        <Link
                          to="#"
                          title="Download"
                          className="fs-14"
                          onClick={() => {
                            window.open(
                              LUTCertificateAttachmentFilePath,
                              "_blank"
                            );
                          }}
                        >
                          {SVGICON.Download}
                        </Link>
                      ) : (
                        ""
                      )}
                    </div>
                    <input
                      type="file"
                      id="fileUpload"
                      className="form-control form-control-sm"
                      accept=".doc,.docx,.pdf,image/jpeg,image/png,image/gif"
                      onChange={uploadLUTCertificateAttachmentFile}
                      key={LUTCertificateFileKey || ""}
                    />
                    <label htmlFor="fileUpload" name=""></label>
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Validity From Date"
                      name="ValidityFromDate"
                      placeholder={"Select Validity From Date"}
                      component={CustomFlatpickr}
                      required
                      options={{
                        altInput: true,
                        altFormat: "j-M-Y",
                        dateFormat: "d-m-Y",
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Validity To Date"
                      name="ValidityToDate"
                      placeholder={"Select Validity To Date"}
                      component={CustomFlatpickr}
                      required
                      options={{
                        altInput: true,
                        altFormat: "j-M-Y",
                        dateFormat: "d-m-Y",
                      }}
                    />
                  </Col>
                </Row>

                <hr className="mt-1 mb-2"></hr>
                <button
                  type="submit"
                  // className="btn btn-primary"
                  className="btn btn-primary btn-sm me-2"
                >
                  <i className="fa-solid fa-floppy-disk"></i> Submit
                </button>
                <button
                  type="button"
                  // className="btn btn-danger light"
                  className="btn btn-danger btn-sm me-2"
                  onClick={(e) => {
                    navigate(-1);
                  }}
                >
                  <i className="fa fa-cancel"></i> Cancel
                </button>
              </Form>
            </FormikProvider>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default AddCompanyLUTDocument;
